import './Table.scss'

import {
  Box,
  EllipsisVIcon,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  SlidersHIcon,
  Theme,
  ThemeProvider,
  Typography,
  useTheme,
} from '@forgeglobal/design-framework'
import {
  ColDef,
  ColumnApi,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  PostProcessPopupParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { ColumnsChooserMenu } from 'isolated-design-library/components/tables/ColumnsChooserMenu'
import { LinkRenderer } from 'isolated-design-library/components/tables/renderers/LinkRenderer'
import { LoadingRenderer } from 'isolated-design-library/components/tables/renderers/LoadingRenderer'
import { TableHeader } from 'isolated-design-library/components/tables/TableHeader'
import numeral from 'numeral'
import React, { Fragment, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { animated, useSpring } from 'react-spring'

import { getTableState, persistTableState, resetTableState, restoreTableState } from './PersistTableState'
import { TableMuiDatePicker } from './renderers/ClassDatePicker'

const useStyles = makeStyles((theme: Theme) => ({
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  fullScreenTable: {
    height: 'initial !important', // override ag-grid's inline height: 100%
    position: 'absolute',
    top: theme.spacing(11), // The height of table header bar
    bottom: 0,
    left: 0,
    right: 0,
    flexGrow: 1,
  },
  header: {
    textTransform: 'none',
  },
}))

const formatYesNo = (params: ValueFormatterParams) => {
  if (params?.value === null || typeof params === undefined) return ''
  if (typeof params.value === 'string') return params.value.toLowerCase() === 'true' ? 'Yes' : 'No'
  return params.value ? 'Yes' : 'No'
}

export const booleanTableFilterParams: any = {
  newRowsAction: 'keep',
  values: [true, false],
}

export const defaultTableBooleanColumn = {
  filterParams: booleanTableFilterParams,
  valueFormatter: formatYesNo,
  filter: 'agSetColumnFilter',
}

export const defaultTextOptions = {
  filterParams: { filterOptions: ['contains'], suppressAndOrCondition: true },
}
export const defaultNumbDateOptions = {
  filterParams: { filterOptions: ['greaterThan'], suppressAndOrCondition: true },
}

export const noInteractionColumnHeader = {
  width: 48,
  minWidth: 48,
  maxWidth: 48,
  cellRenderer: 'link',
  suppressMenu: true,
  suppressMovable: true,
  sortable: false,
  lockPinned: true,
  cellStyle: { padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  suppressSizeToFit: true,
  headerClass: 'ag-grid-no-hover',
  enableValue: false,
  filterParams: { values: [] }, // dummy values
}
const frameworkComponents = {
  loading: LoadingRenderer,
  agColumnHeader: TableHeader,
  link: LinkRenderer,
  agDateInputAlt: TableMuiDatePicker,
}

export const gridOptions: GridOptions = {
  defaultColDef: {
    sortable: true,
    resizable: true,
    filter: true,
    filterParams: {
      newRowsAction: 'keep',
      clearButton: true,
    },
    enableRowGroup: false,
    enablePivot: false,
    enableValue: true,
    menuTabs: ['filterMenuTab'],
  },
  getContextMenuItems: () => ['copy', 'copyWithHeaders', 'paste'],
  reactNext: true, // needed if React 16+ is used
  rowModelType: 'serverSide', // use the server-side row model
  cacheBlockSize: 10, // fetch 10 rows per at a time
  maxBlocksInCache: 200, // keep 200 blocks of rows
  cacheOverflowSize: 1,
  infiniteInitialRowCount: 100,
  animateRows: true,
  accentedSort: true, // The sort should take into account accented characters; Sort will be slower
  enterMovesDown: true,
  headerHeight: 48,
  sideBar: false,
  context: { componentParent: this },
  loadingCellRenderer: 'loading',
  frameworkComponents,
  dateComponent: 'agDateInputAlt',
}

export interface TableProps extends AgGridReactProps {
  className?: string
  persistStateForDimensions?: {
    tableName: string
    userId: string | number
    [dimension: string]: string | number
  }
  children?: React.ReactNode
  isFullScreen?: boolean
  handleGridReady?: (gridApi: GridApi) => void
  handleGridReset?: (gridApi: GridApi) => void
  handleFilterChanged?: (event: FilterChangedEvent) => void
}

export const Table = ({
  gridOptions: customGridOptions,
  serverSideDatasource,
  columnDefs,
  className,
  persistStateForDimensions,
  children,
  isFullScreen,
  handleGridReady,
  handleGridReset,
  handleFilterChanged,
  ...rest
}: TableProps) => {
  const theme = useTheme()
  const styles = useStyles()
  let [gridApi, setGridApi] = useState<GridApi | null>(null)
  let [columnApi, setColumnApi] = useState<ColumnApi | null>(null)
  const [moreAnchor, setMoreAnchor] = useState<Element | null>(null)
  const [numRows, setNumRows] = useState<number | null>(null)
  const [columnsChooserMenuOpen, setColumnsChooserMenuOpen] = useState(false)
  const tableRef = useRef(null)

  // Clear button always set in filter params
  columnDefs &&
    columnDefs.forEach(c => {
      if ('filterParams' in c && (c as ColDef).filterParams.clearButton !== false) {
        ;(c as ColDef).filterParams.clearButton = true
      }
      ;(c as ColDef).sortable = false
    })

  // Detect when the first data is retrieved from the server
  class ServerSideDataSourceWrapper implements IServerSideDatasource {
    getRows(params: IServerSideGetRowsParams) {
      serverSideDatasource!.getRows({
        ...params,
        successCallback: (rowsThisPage: any[], lastRow: number) => {
          params.successCallback(rowsThisPage, lastRow)
          // It's possible to use gridApi.getModel().getRowCount()
          // But this results in a row count of 1 when the table is loading
          setNumRows(lastRow)
        },
      })
    }

    destroy() {
      serverSideDatasource!.destroy && serverSideDatasource!.destroy()
    }
  }

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api)
    setColumnApi(params.columnApi)
    gridApi = params.api // unsure why, but gridApi doesn't update in time
    columnApi = params.columnApi // idem

    const initialize = (res?: any) => {
      // Load the data source after the table loads, to avoid it making two network requests and flickering
      // Introduces a small delay
      // https://github.com/ag-grid/ag-grid/issues/2275
      if (gridApi) {
        gridApi.setServerSideDatasource(new ServerSideDataSourceWrapper())
        if (handleGridReady) {
          handleGridReady(gridApi)
        }
      }

      columnDefs && gridApi && gridApi.setColumnDefs(columnDefs)

      return res
    }

    if (persistStateForDimensions) {
      getTableState(persistStateForDimensions)
        .then(initialize)
        .then(res => gridApi && columnApi && restoreTableState(gridApi, columnApi, res))
    } else {
      initialize()
    }
  }

  const autosize = () => {
    columnApi && columnApi.autoSizeAllColumns()
    setMoreAnchor(null)
  }

  const reset = () => {
    columnApi && columnApi.resetColumnState()
    if (gridApi) {
      // Reset filtering and sorting
      gridApi.setFilterModel(null)
      gridApi.setSortModel(null)
      if (handleGridReset) {
        handleGridReset(gridApi)
      }
      gridApi.onFilterChanged()
      gridApi.onSortChanged()
      gridApi.refreshHeader()
    }
    persistStateForDimensions && resetTableState(persistStateForDimensions)
    setMoreAnchor(null)
  }

  const showColumnsChooser = () => {
    setColumnsChooserMenuOpen(true)
    setMoreAnchor(null)
  }

  const persistState = () => {
    // For some reason, the onFilterModified/onFilterChanged hooks call this before, not after the change.
    // This is a workaround to make sure the new value, not the old value is persisted.
    setTimeout(
      () =>
        columnApi &&
        gridApi &&
        persistStateForDimensions &&
        persistTableState(gridApi, columnApi, persistStateForDimensions),
      0
    )
  }

  const finalGridOptions: GridOptions = {
    onGridReady: onGridReady,
    postProcessPopup: (params: PostProcessPopupParams) => {
      if (params.type !== 'columnMenu') return
      // TODO: ag-grid implementation-dependent. Bad practice
      const header = params.ePopup.querySelector('.ag-tab-header')
      if (header)
        header.innerHTML = renderToString(
          <Fragment>
            <ThemeProvider>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <SlidersHIcon style={{ fontSize: '35px' }} />
                <Typography variant="h6" style={{ marginLeft: '12px' }}>
                  {params.eventSource && params.eventSource.innerText}
                </Typography>
              </div>
            </ThemeProvider>
          </Fragment>
        )
      // Position the popup so it's flush with the top of the cell header
      params.ePopup.style.top = '2px'
      // The popup shouldn't overflow from the left of the table
      const left = parseInt((params.ePopup.style.left || '').replace(/\D/g, ''), 10) || 0
      params.ePopup.style.left = `${Math.max(2, left)}px`

      // Clear button closes the modal
      setTimeout(() => {
        // give a chance to the content of the modal to render
        const clearButton = params.ePopup.querySelector('[ref=eClearButton]')
        clearButton && clearButton.addEventListener('click', () => gridApi && gridApi.hidePopupMenu())
      }, 0)
    },
    onFilterModified: () => {
      if (gridApi) {
        gridApi.refreshHeader()
        persistState()
      }
    },
    onFilterChanged: (event: FilterChangedEvent) => {
      if (handleFilterChanged) {
        handleFilterChanged(event)
      }
    },
    onColumnMoved: persistState,
    onColumnPinned: persistState,
    onColumnVisible: persistState,
    onSortChanged: persistState,
    onColumnEverythingChanged: persistState,
    onGridColumnsChanged: persistState,
    ...gridOptions,
    ...customGridOptions,
  }
  const [scrollPos, setScrollPos] = useState(0)
  const onScroll = (evt: any) => {
    const newPos = scrollPos + evt.deltaY
    setScrollPos(newPos > 0 ? newPos : 0)
  }
  const isScrolled = scrollPos > 0
  const tableStyles = useSpring({
    top: isScrolled ? theme.spacing(11) : theme.spacing(11),
    flexBasis: isScrolled ? '0%' : '100%',
    flexGrow: isScrolled ? 1 : 0,
    marginTop: isScrolled ? theme.spacing(0) : theme.spacing(4),
    marginBottom: isScrolled ? theme.spacing(-0.5) : theme.spacing(2),
  })
  return (
    <div className={'ag-theme-material ag-grid-has-custom-header ' + className}>
      <div className={styles.tableHeader}>
        <div>
          <Box ml={2}>
            <Typography color="secondary" className={styles.header}>
              <div>{children}</div>
            </Typography>
          </Box>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            style={{ marginRight: theme.spacing(1) }}
            color="textSecondary"
            variant="subtitle2"
            data-cy="tableRowCount">
            {numRows && numeral(numRows).format('0,0')}
          </Typography>
          <IconButton onClick={e => setMoreAnchor(e.currentTarget)}>
            <EllipsisVIcon />
          </IconButton>
        </div>
        <Menu
          anchorEl={moreAnchor}
          keepMounted
          open={!!moreAnchor}
          onClose={() => setMoreAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <MenuItem onClick={showColumnsChooser} data-cy="chooseColumnsButton">
            Choose columns
          </MenuItem>
          <MenuItem onClick={reset} data-cy="resetColumnsButton">
            Reset columns
          </MenuItem>
          <MenuItem onClick={autosize}>Autosize columns</MenuItem>
        </Menu>
        {gridApi && columnApi && columnsChooserMenuOpen && (
          <ColumnsChooserMenu
            anchorEl={tableRef.current ? (tableRef.current as any).eGridDiv : null}
            open={columnsChooserMenuOpen}
            onClose={() => setColumnsChooserMenuOpen(false)}
            gridApi={gridApi}
            columnApi={columnApi}
          />
        )}
      </div>
      <animated.div
        onWheel={onScroll}
        className={isFullScreen ? styles.fullScreenTable : ''}
        style={{ top: tableStyles.top }}>
        <AgGridReact ref={tableRef} gridOptions={finalGridOptions} {...rest} />
      </animated.div>
    </div>
  )
}
